import * as Sentry from '@sentry/react';
import constate from 'constate';
import { useCallback, useState } from 'react';
import type { QueryObserverResult } from 'react-query';

import { resetAnalyticsUser, trackAnalyticsEvent } from '../../Analytics';
import { ApiClient, ApiError } from '../../Api';
import { BrowserStorageType, clearBrowserStorage } from '../../BrowserStorage';
import { useLaunchDarklyUpdater } from '../../FeatureFlags';
import { appendQueryParams } from '../../UrlQueryParam';
import type { IColorUser } from '../ColorUser';

import { SESSIONS_ENDPOINT, useAuthSession, useAuthenticatedUser } from './apis';
import { AUTH_HEARTBEAT_INTERVAL_IN_MSEC, HTTP_UNATHENTICATED_STATUSES } from './constants';
import { useIdentifySegmentUser } from './Segment/useIdentifySegmentUser';

interface AuthStateProps {
  baseUrl?: string;
  sessionRefetchIntervalMs?: number; // Interval of time (ms) between a refetch for user session data
  /* Continue to refetch session on interval while tab/window is in the background; this
   * is recommended if the refetch interval is high. Otherwise, if the user frequently
   * flips between windows/tabs, the actual time between refetches can be much longer than
   * the configured refetchInterval */
  sessionRefetchIntervalInBackground?: boolean;
  authUserStaleTimeMs?: number; // Amount of time (ms) until a user's auth data is considered "stale"
}

/**
 * When a user signs out, or the app wants to reset auth state, we should also
 * clear out session storage to remove lingering data in the browser state.
 */
function clearSessionState(): void {
  clearBrowserStorage(BrowserStorageType.SESSION_STORAGE);
}

interface AuthStateResult {
  currentUser: IColorUser | undefined;
  hasInitializedUser: boolean;
  hasInitializedSession: boolean;
  isAuthenticated: boolean;
  resetStateAndUpdateSession: () => Promise<void>;
  signIn: (
    email: string,
    password: string,
    nextUrl?: string
  ) => Promise<{
    isSuccessful: boolean;
    mfaConfig?: any;
    emailNeedsConfirmation?: boolean;
  }>;
  signOut: () => Promise<void>;
  fetchUser: () => Promise<QueryObserverResult<IColorUser | undefined>>;
}

export function useAuthState(props: AuthStateProps = {}): AuthStateResult {
  // Apply defaults if unset in props
  const {
    baseUrl = '',
    sessionRefetchIntervalMs = AUTH_HEARTBEAT_INTERVAL_IN_MSEC,
    sessionRefetchIntervalInBackground = false,
    authUserStaleTimeMs = 0,
  } = props;

  /*
   * FIXME: `isAuthenticated` is the same as `sessionResponse.isSuccess`. This state is redundant.
   * This state is currently also tied to `signIn` and `signOut` responses, which probably should be decoupled.
   */
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const { updateSegmentUser } = useIdentifySegmentUser();
  const { updateLaunchDarkly } = useLaunchDarklyUpdater();
  const resetState = useCallback(() => {
    clearSessionState();
    setIsAuthenticated(false);
  }, []);

  const sessionResponse = useAuthSession(
    {
      retry: false,
      refetchInterval: sessionRefetchIntervalMs,
      refetchIntervalInBackground: sessionRefetchIntervalInBackground,
      onSuccess: () => {
        setIsAuthenticated(true);
      },
      onError: (error) => {
        // API logs error to console. Not logging here on purpose.
        if (HTTP_UNATHENTICATED_STATUSES.has(error?.status)) {
          // Only unauthenticate if:
          // 1. response status indicates. (Ie: not on redirects or 500s)
          // 2. This is the initial load (page load, or after resetState)
          setIsAuthenticated(false);
        }
      },
      onSettled: async () => {
        await updateLaunchDarkly();
      },
    },
    baseUrl
  );

  const authenticatedUserResponse = useAuthenticatedUser(
    isAuthenticated,
    {
      refetchOnWindowFocus: isAuthenticated,
      staleTime: authUserStaleTimeMs,
      onSuccess: (user: IColorUser | undefined) => {
        updateSegmentUser(user);
        // This just sets some local state, so no need to memoize
        Sentry.setUser(user ? { id: user.id.toString() } : null);
      },
    },
    baseUrl
  );

  const {
    data: currentUser,
    refetch: fetchUser,
    isFetched: userIsFetched,
    remove: clearAuthenticatedUser,
  } = authenticatedUserResponse;

  const {
    isFetched: hasInitializedSession,
    refetch: updateSession,
    error: sessionError,
  } = sessionResponse;

  const signIn = useCallback(
    async (email: string, password: string, nextUrl?: string) => {
      try {
        const createSessionUrl = appendQueryParams(`${baseUrl}${SESSIONS_ENDPOINT}`, {
          next: nextUrl,
        });
        // FIXME - convert to `useMutation`
        const response = await ApiClient.post(createSessionUrl, {
          json: {
            email,
            password,
          },
        });

        // A successful response means password was correct, but it does not
        // mean the user was logged in, as other special circumstances may have
        // arisen
        const isMfaRequired = response?.is_mfa_required ?? false;
        if (isMfaRequired) {
          setIsAuthenticated(false);
          return {
            isSuccessful: false,
            mfaConfig: { factors: response?.factors ?? [] },
          };
        }

        const emailNeedsConfirmation = response?.email_needs_confirmation ?? false;
        if (emailNeedsConfirmation) {
          setIsAuthenticated(false);
          return {
            isSuccessful: false,
            emailNeedsConfirmation: true,
          };
        }

        trackAnalyticsEvent('Sign In - Succeeded', {
          is_mfa: 'no',
        });
        setIsAuthenticated(true);
        await updateLaunchDarkly();
        return { isSuccessful: true };
      } catch (error) {
        trackAnalyticsEvent('Sign In - Failed', {
          error: error instanceof Error ? error.message : (error?.toString?.() ?? ''),
          status: error instanceof ApiError ? error.status : '',
        });
        setIsAuthenticated(false);
        throw error;
      }
    },
    [updateLaunchDarkly, baseUrl]
  );

  const signOut = useCallback(async () => {
    try {
      // FIXME - convert to `useMutation`
      await ApiClient.delete(`${baseUrl}${SESSIONS_ENDPOINT}`);
    } catch (error) {
      if ((error as ApiError).status !== 401) {
        // If the API responds with 401 (Unauthorized) then the session is already signed out,
        // and can ignore the error.
        // If the API failure is for any other reason, we should still treat this as an error.
        throw error;
      }
    }

    clearAuthenticatedUser();
    clearSessionState();
    resetAnalyticsUser();
    Sentry.setUser(null);
    setIsAuthenticated(false);
    try {
      // This indirectly calls updateLaunchDarkly()
      await updateSession();
    } catch {
      // This is expected to fail, and it's only called to ensure the
      // session data is updated accordingly
    }
  }, [clearAuthenticatedUser, updateSession, baseUrl]);

  /**
   * The session response could fail for a number of reasons, including intermittent network connections.
   * The error is considered as unauthenticated if the API status code is one of `HTTP_UNATHENTICATED_STATUSES`
   */
  const sessionErrorIsUnauthenticatedStatus =
    hasInitializedSession &&
    sessionError != null &&
    HTTP_UNATHENTICATED_STATUSES.has(sessionError.status);

  const resetStateAndUpdateSession = useCallback(async () => {
    resetState();

    const updateSessionResponse = await updateSession();
    let attemptCount = 1;
    let hasSucceeded = updateSessionResponse.isSuccess;
    if (!hasSucceeded) {
      // Apparently, `react-query` can run into a race condition triggered by manually running a query that is
      // scheduled to run at regular intervals, causing a request to not have all expected cookies.
      // In this case, it might happen we try to update the session without a session cookie, even though we expect
      // the user to be logged in already, and so we need to retry once to circumvent it.
      const retriedUpdateSessionResponse = await updateSession();
      attemptCount += 1;
      hasSucceeded = retriedUpdateSessionResponse.isSuccess;
    }
    trackAnalyticsEvent('Session Refresh', {
      status: hasSucceeded ? 'succeeded' : 'failed',
      attempt_count: attemptCount,
    });
  }, [resetState, updateSession]);

  return {
    currentUser,
    hasInitializedUser: userIsFetched || sessionErrorIsUnauthenticatedStatus,
    hasInitializedSession,
    isAuthenticated,
    resetStateAndUpdateSession,
    signIn,
    signOut,
    fetchUser,
  };
}

const dest = constate(useAuthState);
export const AuthProvider: React.FC<React.PropsWithChildren<AuthStateProps>> = dest[0];
export const useAuthContext: () => AuthStateResult = dest[1];
